// frontend/src/pages/HomePage.jsx
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../services/api'
import { content as hardcodedContent } from '../../config/content'


// Composant utilitaire pour les sections avec fond beige
const Section = ({ className, children, bgColor = "bg-white" }) => (
  <section className={`${bgColor} py-16 md:py-24 ${className || ''}`}>
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      {children}
    </div>
  </section>
)

export default function HomePage() {
  const[content, setContent] = useState(null)
  const [identity, setIdentity] = useState(null)

  useEffect(() => {
      api.get('/api/workspaces/public-profile')
         .then(res => {
             setContent(res.data.landing_config)
             setIdentity({
                name: res.data.admin_name,
                title: res.data.admin_title,
                email: res.data.cabinet_email,
                phone: res.data.cabinet_phone,
                price_new: res.data.price_new,
                price_followup: res.data.price_followup
             })
         })
         .catch(console.error)
  },[])

  if (!content || !identity) {
      return <div className="min-h-screen flex items-center justify-center"><div className="spinner border-sage-600" /></div>
  }

  // On extrait les variables de l'API, MAIS on force l'utilisation des 'locations' et 'specialties' du fichier en dur !
  const { hero, philosophy, pricing, approach, contact } = content
  const locations = hardcodedContent.locations
  const specialties = hardcodedContent.specialties


  // Couleur texte principale (Marron foncé du Canva)
  const textColor = "text-[#544541]"

  // Couleur fond beige du Canva
  const beigeBg = "bg-[#ECEADA]"

  return (
    <div className="flex flex-col min-h-screen">
      
      {/* --- 1. HERO SECTION --- */}
      <Section>
        {/* En-tête "BIENVENUE" */}
        <div className="mb-8 border-b border-gray-300 pb-2">
          <h2 className={`uppercase tracking-widest text-lg ${textColor} font-sans`}>
            {hero.welcome}
          </h2>
        </div>

        <div className="grid md:grid-cols-12 gap-12 items-center">
          {/* Image Bureau */}
          <div className="md:col-span-8">
            <img 
              src={hero.image} 
              alt="Cabinet Diététique" 
              className="w-full h-auto rounded-[2rem] shadow-sm object-cover aspect-[4/3]"
            />
          </div>
          
          {/* Titre Principal */}
          <div className="md:col-span-4 flex flex-col justify-center text-center md:text-left">
            <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl leading-tight mb-4 text-[#735E59]">
              {identity.name.split(' ').map((word, i) => (
                <span key={i} className="block">{word}</span>
              ))}
            </h1>
            <p className={`text-2xl md:text-3xl ${textColor} font-sans font-light`}>
              {hero.subtitle}
            </p>
          </div>
        </div>
      </Section>

      {/* --- 2. PHILOSOPHIE --- */}
      <Section bgColor={beigeBg}>
        <div className="grid md:grid-cols-2 gap-12 md:gap-24 items-center">
          {/* Texte */}
          <div className="order-2 md:order-1">
            <h2 className="font-serif text-5xl md:text-6xl text-[#735E59] mb-8">
              {philosophy.title}
            </h2>
            <h3 className={`font-extrabold font-sans text-xl mb-6 ${textColor}`}
              dangerouslySetInnerHTML={{ __html: philosophy.subtitle }}
            />
            <div className={`space-y-4 text-lg ${textColor} leading-relaxed`}>
              {philosophy.text.map((p, index) => (
                <p 
                  key={index}
                  dangerouslySetInnerHTML={{ __html: p }}
                  />
              ))}
            </div>
            <p className={`mt-8 italic font-serif text-xl ${textColor}`}
              dangerouslySetInnerHTML={{ __html: philosophy.quote }}
            />
          </div>

          {/* Photo Portrait */}
          <div className="order-1 md:order-2 flex justify-center">
            {/* 1. LE CADRE (La fenêtre) */}
            {/* C'est lui qui porte l'arrondi, l'ombre et la taille */}
            <div className="w-full max-w-xs rounded-[3rem] overflow-hidden shadow-lg">
              
              {/* 2. L'IMAGE (Le contenu) */}
              {/* On utilise une marge négative pour la remonter et "manger" le haut */}
              <img 
                src={philosophy.image} 
                alt={identity.name} 
                className="w-full h-auto -mt-[35%]" 
                style={{ display: 'block' }} // Sécurité pixel blanc
              />
            </div>
          </div>
        </div>
      </Section>

      {/* --- 3. SPECIALITES (Consultation adaptée) --- */}
      <Section>
        <h2 className="font-serif text-4xl md:text-5xl text-[#735E59] mb-12 border-b border-gray-200 pb-4 inline-block">
          {specialties.title}
        </h2>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8">
          {specialties.items.map((item, idx) => (
            <div key={idx} className="flex flex-col items-center text-center group">
              <div className="w-full aspect-square rounded-[2rem] overflow-hidden mb-4 shadow-sm group-hover:shadow-md transition-shadow">
                <img 
                  src={item.image} 
                  alt={item.label} 
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <p className={`font-bold text-sm md:text-base ${textColor} whitespace-pre-line`}>
                {item.label}
              </p>
            </div>
          ))}
          {/* Élément "Autre" (Texte seul comme sur la maquette) */}
          <div className="flex flex-col items-center justify-center text-center h-full">
             <p className={`font-serif font-bold italic text-xl ${textColor}`}>Autre...</p>
          </div>
        </div>
      </Section>

      {/* --- 4. LIEUX DE CONSULTATION --- */}
      <Section bgColor={beigeBg}>
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Liste des lieux */}
          <div>
            <h2 className="font-serif text-5xl md:text-6xl text-[#735E59] mb-12">
              {locations.title}
            </h2>
            <div className="space-y-10">
              {locations.list.map((loc, idx) => (
                <div key={idx} className="border-l-4 border-[#735E59] pl-6">
                  <h3 className={`font-bold text-xl ${textColor}`}>{loc.name}</h3>
                  {loc.address && <p className={`text-lg ${textColor}`}>{loc.address}</p>}
                  {loc.details && (
                    <p className={`text-[#544541] opacity-80 mt-1 whitespace-pre-line`}>
                      {loc.details}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Images empilées */}
          <div className="space-y-8 flex flex-col justify-center">
            {locations.images.map((img, idx) => (
              <img 
                key={idx} 
                src={img} 
                alt="Cabinet" 
                className="w-full rounded-[2rem] shadow-md object-cover h-64 md:h-80"
              />
            ))}
          </div>
        </div>
      </Section>

      {/* --- 5. TARIFS & APPROCHE --- */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Colonne Gauche : Tarifs + Remboursement */}
          <div>
            <h2 className="font-serif text-5xl md:text-6xl text-[#735E59] mb-12">
              {pricing.title}
            </h2>
            

            {/* Certif AFDN */}
            <div className="mb-12">
              <p className={`text-lg ${textColor} mb-4 whitespace-pre-line font-medium`}
                dangerouslySetInnerHTML={{ __html: pricing.certif.text }}
              />
            </div>

            {/* Prix Dynamiques Mises à Jour */}
            <div className="flex justify-between items-center mb-12">
              
              {/* Colonne des Prix */}
              <div className="space-y-8">
                <div className="flex flex-col">
                  <span className={`font-bold text-lg ${textColor}`}>Première consultation</span>
                  <span className={`text-lg font-light ${textColor}`}>
                    {identity.price_new ? `${identity.price_new}€` : '60€'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className={`font-bold text-lg ${textColor}`}>Suivi</span>
                  <span className={`text-lg font-light ${textColor}`}>
                    {identity.price_followup ? `${identity.price_followup}€` : '40€'}
                  </span>
                </div>
              </div>

              {/* Logo AFDN */}
              <img 
                src={pricing.certif.logo} 
                alt="Logo AFDN" 
                className="h-24 object-contain ml-4" // ml-4 ajoute une petite marge à gauche pour ne pas coller
              />
            </div>

            {/* Remboursement */}
            <div>
              <h3 className={`font-bold text-2xl ${textColor} mb-2`}>{pricing.refund.title}</h3>
              <p className={`text-lg ${textColor}`}
                dangerouslySetInnerHTML={{ __html: pricing.refund.text }}
              />
            </div>
          </div>

          {/* Colonne Droite : Approche + CTA */}
          <div className="flex flex-col h-full">
            <div className="mb-12">
              <h3 className={`font-bold text-2xl ${textColor} mb-6`}>
                {approach.title}
              </h3>
              <ul className="space-y-4">
                {approach.points.map((point, idx) => (
                  <li key={idx} className={`flex items-start ${textColor}`}>
                    <span className="mr-3 text-[#735E59] text-xl">•</span>
                    <span className="text-lg">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* GROS BOUTON CTA (Style Bulle) */}
            <div className="mt-auto">
              <Link 
                to="/booking"
                className="block w-full bg-white border-4 border-black rounded-[3rem] p-8 md:p-10 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200 group text-center md:text-left"
              >
                <p className="text-xl md:text-2xl text-gray-500 font-serif italic mb-2">
                  Prête à être accompagnée !
                </p>
                <p className="text-2xl md:text-3xl font-bold text-[#544541]">
                  Réservez votre premier créneau en quelques clics.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* --- 6. CONTACT --- */}
      <Section bgColor={beigeBg}>
        <h2 className="font-serif text-5xl md:text-6xl text-[#735E59] mb-12 border-b border-[#735E59] pb-4">
          {contact.title}
        </h2>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Images */}
          <div className="grid grid-cols-2 gap-4">
            {contact.images.map((img, idx) => (
              <img 
                key={idx} 
                src={img} 
                alt="Ambiance" 
                className="w-full h-full object-cover rounded-lg aspect-[5/5]"
              />
            ))}
          </div>

          {/* Info Textes */}
          <div className="flex flex-col justify-center space-y-12">
            <p className={`text-xl ${textColor}`}>
              {contact.intro}
            </p>

            <div>
              <h3 className={`uppercase font-bold tracking-widest text-sm mb-2 ${textColor}`}>
                Téléphone
              </h3>
              <p className={`text-2xl ${textColor}`}>{identity.phone}</p>
            </div>

            <div>
              <h3 className={`uppercase font-bold tracking-widest text-sm mb-2 ${textColor}`}>
                Email
              </h3>
              <a href={`mailto:${identity.email}`} className={`text-2xl ${textColor} hover:underline`}>
                {identity.email}
              </a>
            </div>
          </div>
        </div>
      </Section>

    </div>
  )
} 